import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Upload, Image, CheckCircle, LogOut, Loader2, X, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'de8bcu0p2';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'nursery_gallery';

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);

  // Check session on mount
  useEffect(() => {
    const session = sessionStorage.getItem('adminLoggedIn');
    if (session === 'true') {
      setIsLoggedIn(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === 'Sacredheart' && password === 'Sacred@2026') {
      setIsLoggedIn(true);
      sessionStorage.setItem('adminLoggedIn', 'true');
      setLoginError('');
    } else {
      setLoginError('Invalid username or password');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    sessionStorage.removeItem('adminLoggedIn');
    setUsername('');
    setPassword('');
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    
    // Generate previews
    const previewUrls = files.map(file => URL.createObjectURL(file));
    setPreviews(previewUrls);
    setUploadSuccess(false);
  };

  const removeFile = (index) => {
    const newFiles = [...selectedFiles];
    const newPreviews = [...previews];
    URL.revokeObjectURL(newPreviews[index]);
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadSuccess(false);
    const uploaded = [];

    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', UPLOAD_PRESET);
        formData.append('folder', 'sacred_heart_gallery');
        // IMPORTANT: Add tag for Cloudinary Resource List API to work
        formData.append('tags', 'sacred_heart_gallery');

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
          {
            method: 'POST',
            body: formData,
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log('Upload successful:', {
            public_id: data.public_id,
            tags: data.tags,
            url: data.secure_url
          });
          uploaded.push({
            url: data.secure_url,
            public_id: data.public_id,
          });
        } else {
          const errorData = await response.json();
          console.error('Upload failed:', errorData);
        }
      }

      setUploadedImages(prev => [...uploaded, ...prev]);
      setUploadSuccess(true);
      setSelectedFiles([]);
      setPreviews([]);
      
      // Clear success message after 5 seconds
      setTimeout(() => setUploadSuccess(false), 5000);
    } catch (error) {
      console.error('Upload error:', error);
    }

    setUploading(false);
  };

  // Login Form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-100 via-white to-slate-100 flex items-center justify-center" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md p-8"
        >
          <div className="bg-white rounded-[2rem] shadow-xl p-8 border border-slate-100">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Image className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-3xl text-slate-800 tracking-wider">ADMIN LOGIN</h1>
              <p className="font-body text-slate-500 mt-2">Sacred Heart School Gallery Manager</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              {loginError && (
                <div className="bg-red-50 text-red-600 p-4 rounded-xl font-body text-sm">
                  {loginError}
                </div>
              )}

              <div>
                <label className="block font-body font-semibold text-slate-700 mb-2">Username</label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-400 focus:ring-4 focus:ring-red-400/20 font-body"
                  placeholder="Enter username"
                  required
                  data-testid="admin-username"
                />
              </div>

              <div>
                <label className="block font-body font-semibold text-slate-700 mb-2">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-400 focus:ring-4 focus:ring-red-400/20 font-body"
                  placeholder="Enter password"
                  required
                  data-testid="admin-password"
                />
              </div>

              <Button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-red-500 to-orange-500 text-white font-body font-bold rounded-xl hover:shadow-lg hover:shadow-red-500/30 transition-all"
                data-testid="admin-login-btn"
              >
                Login
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-100 via-white to-slate-100" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-4xl text-slate-800 tracking-wider">GALLERY MANAGER</h1>
            <p className="font-body text-slate-500 mt-1">Upload and manage school gallery images</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="flex items-center gap-2 px-4 py-2 border-2 border-slate-200 rounded-xl font-body hover:border-red-400 hover:text-red-500"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>

        {/* Upload Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[2rem] shadow-lg p-8 mb-8 border border-slate-100"
        >
          <h2 className="font-heading text-2xl text-slate-800 tracking-wider mb-6">UPLOAD IMAGES</h2>

          {/* Success Message */}
          {uploadSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-xl mb-6 flex items-center gap-3"
              data-testid="upload-success"
            >
              <CheckCircle className="w-5 h-5" />
              <span className="font-body">Images uploaded successfully to Cloudinary!</span>
            </motion.div>
          )}

          {/* File Input */}
          <div className="mb-6">
            <label
              htmlFor="file-upload"
              className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-slate-300 rounded-2xl cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-10 h-10 text-slate-400 mb-3" />
                <p className="mb-2 text-sm text-slate-500 font-body">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-slate-400 font-body">PNG, JPG, JPEG (MAX. 10MB)</p>
              </div>
              <input
                id="file-upload"
                type="file"
                className="hidden"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                data-testid="file-input"
              />
            </label>
          </div>

          {/* Preview Images */}
          {previews.length > 0 && (
            <div className="mb-6">
              <h3 className="font-body font-semibold text-slate-700 mb-4">Preview ({selectedFiles.length} files selected)</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {previews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-32 object-cover rounded-xl border-2 border-slate-200"
                    />
                    <button
                      onClick={() => removeFile(index)}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          <Button
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || uploading}
            className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-red-500 to-orange-500 text-white font-body font-bold rounded-xl hover:shadow-lg hover:shadow-red-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 mr-2" />
                Upload to Cloudinary
              </>
            )}
          </Button>
        </motion.div>

        {/* Recently Uploaded */}
        {uploadedImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-[2rem] shadow-lg p-8 border border-slate-100"
          >
            <h2 className="font-heading text-2xl text-slate-800 tracking-wider mb-6">RECENTLY UPLOADED</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {uploadedImages.map((img, index) => (
                <div key={index} className="relative group">
                  <img
                    src={img.url}
                    alt={`Uploaded ${index + 1}`}
                    className="w-full h-32 object-cover rounded-xl border-2 border-green-200"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                    <a
                      href={img.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white"
                    >
                      <Eye className="w-6 h-6" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Admin;
